import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = 'https://api.smart-highlights.ai';
const PROTOCOL = window.location.protocol === 'https:' ? 'wss' : 'ws';
const WS_BASE_URL = `${PROTOCOL}://api.smart-highlights.ai/ws`;

function ProgressBar({ progress, status }) {
    if (!status) return null;
    const isError = status.includes('❌');
    return (
        <div className="w-full mb-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex justify-between items-end mb-2">
                <span className={`text-xs font-bold font-mono uppercase tracking-wider ${isError ? 'text-red-400' : 'text-cyan-400 animate-pulse'}`}>{status}</span>
                {!isError && <span className="text-sm font-black text-white">{progress}%</span>}
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2.5 overflow-hidden border border-slate-700">
                <div className={`h-2.5 rounded-full transition-all duration-300 ease-out ${isError ? 'bg-red-500 w-full' : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]'}`} style={{ width: isError ? '100%' : `${progress}%` }}></div>
            </div>
        </div>
    );
}

function VideoUploader({ onUploadSuccess, user, onCreditsUpdate }) {
    const { t } = useTranslation();
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('');
    const clientIdRef = useRef(`client_up_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    const ws = useRef(null);
    const hasSucceeded = useRef(false);

    useEffect(() => {
        try {
            const wsUrl = `${API_BASE_URL.replace('https', 'wss').replace('http', 'ws')}/ws/${clientIdRef.current}`;
            ws.current = new WebSocket(wsUrl);
            ws.current.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.progress !== undefined) {
                    setProgress(data.progress);
                    setStatus(data.status);
                    
                    if (data.progress === 100 && data.result && data.result.id && !hasSucceeded.current) {
                        hasSucceeded.current = true;
                        onCreditsUpdate(); 
                        setTimeout(() => {
                            onUploadSuccess(data.result.id);
                        }, 1000); 
                    }
                    if (data.progress === -1) {
                        setIsUploading(false); 
                    }
                }
            };
        } catch (e) { console.error("WS Upload Fail", e); }
        
        return () => { if (ws.current) ws.current.close(); };
    }, [onUploadSuccess, onCreditsUpdate]);

    const handleUpload = async () => {
        if (!file || !user) return;
        setIsUploading(true);
        setProgress(2);
        setStatus(t('secure_connection'));

        const formData = new FormData();
        formData.append('file', file);
        formData.append('client_id', clientIdRef.current);
        formData.append('user_id', user.id);
        // 🔥 NOUVEAU : On envoie l'email au backend pour les notifications !
        formData.append('user_email', user.email); 

        try {
            const response = await fetch(`${API_BASE_URL}/upload_video/`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            
            if (data.status === "error") {
                setStatus(t('server_error'));
                setIsUploading(false);
            }
        } catch (err) {
            setStatus(t('network_error'));
            setIsUploading(false);
            console.error(err);
        }
    };

    return (
        <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl max-w-2xl mx-auto shadow-2xl animate-in fade-in zoom-in-95 duration-300">
            <h2 className="text-xl font-black text-white mb-2 flex items-center gap-3">
                <span className="bg-indigo-500/20 text-indigo-400 p-2 rounded-lg">📤</span> 
                {t('new_production')}
            </h2>
            <p className="text-slate-400 text-sm mb-6 ml-11">{t('pricing')} <strong className="text-yellow-400">{t('one_credit_per_min')}</strong> {t('of_video')}</p>
            
            {!isUploading && progress === 0 ? (
                <div className="border-2 border-dashed border-slate-700 hover:border-indigo-500 bg-slate-800/50 rounded-xl p-10 text-center transition-all group">
                    <input type="file" accept="video/mp4,video/x-m4v,video/*" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="video-upload" />
                    
                    {!file ? (
                        <label htmlFor="video-upload" className="cursor-pointer flex flex-col items-center">
                            <span className="text-5xl mb-4 opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all">🎥</span>
                            <span className="text-slate-300 font-bold mb-2">{t('click_to_select')}</span>
                            <span className="text-slate-500 text-xs font-mono">{t('formats')}</span>
                        </label>
                    ) : (
                        <div className="flex flex-col items-center">
                            <div className="bg-indigo-500/20 text-indigo-300 px-4 py-2 rounded-lg font-mono text-sm mb-6 border border-indigo-500/30">
                                📁 {file.name}
                            </div>
                            <button onClick={handleUpload} className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg shadow-indigo-500/20 hover:scale-105 active:scale-95 transition-all w-full flex items-center justify-center gap-2">
                                <span>✨</span> {t('start_analysis')}
                            </button>
                            <label htmlFor="video-upload" className="mt-4 text-xs text-slate-500 hover:text-white cursor-pointer underline">
                                {t('choose_another')}
                            </label>
                        </div>
                    )}
                </div>
            ) : (
                <div className="py-8">
                    <ProgressBar progress={progress} status={status} />
                    {progress !== -1 && <p className="text-center text-xs text-slate-500 font-mono mt-4">{t('processing_warning')}</p>}
                    {progress === -1 && <button onClick={() => {setProgress(0); setIsUploading(false);}} className="mx-auto block mt-4 text-sm text-indigo-400 hover:text-white">← {t('retry')}</button>}
                </div>
            )}
        </div>
    );
}

function MontageGenerator({ videoId, initialHighlights }) {
  const { t } = useTranslation();
  const [highlights, setHighlights] = useState(initialHighlights || []);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [montageUrl, setMontageUrl] = useState(null);
  const [renderFormat, setRenderFormat] = useState('portrait'); 
  const [withSubtitles, setWithSubtitles] = useState(true);
  const [subStyle, setSubStyle] = useState('cyan');
  
  const clientIdRef = useRef(`client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [progress, setProgress] = useState(0);
  const [wsStatus, setWsStatus] = useState(null);
  const ws = useRef(null);

  useEffect(() => {
    if (initialHighlights && Array.isArray(initialHighlights)) setHighlights(initialHighlights);
    try {
        const wsUrl = `${API_BASE_URL.replace('https', 'wss').replace('http', 'ws')}/ws/${clientIdRef.current}`;
        ws.current = new WebSocket(wsUrl);
        ws.current.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.progress !== undefined) {
                setProgress(data.progress);
                setWsStatus(data.status);
            }
        };
    } catch (e) { console.error("WS Fail", e); }
    return () => { if (ws.current) ws.current.close(); };
  }, [initialHighlights]);

  const toggleSegment = (index) => {
    if (montageUrl) setMontageUrl(null); 
    if (selectedSegments.includes(index)) setSelectedSegments(selectedSegments.filter(i => i !== index));
    else setSelectedSegments([...selectedSegments, index].sort((a, b) => a - b));
  };

  const generateMontage = async () => {
    if (selectedSegments.length === 0) return;
    setIsGenerating(true); setMontageUrl(null); setProgress(0); setWsStatus(t('secure_connection'));
    try {
      const response = await fetch(`${API_BASE_URL}/generate_montage/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_id: videoId, selected_segments: selectedSegments, render_format: renderFormat, with_subtitles: withSubtitles, style_preset: subStyle, client_id: clientIdRef.current })
      });
      const data = await response.json();
      if (data.output_file) {
        setMontageUrl(`${API_BASE_URL}/download_montage/${videoId}?filename=${data.output_file}`);
        setWsStatus("✅"); setProgress(100);
      }
    } catch (e) { setWsStatus("❌"); console.error(e); } finally { setIsGenerating(false); }
  };

  return (
    <div className="bg-slate-900 text-slate-100 p-6 rounded-xl border border-slate-800 shadow-2xl">
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-6 bg-slate-800/50 p-4 rounded-xl border border-slate-700">
        <div className="flex bg-slate-900 p-1 rounded-lg border border-slate-700">
           <button className={`px-4 py-2 rounded-md transition-all text-xs font-bold flex gap-2 items-center ${renderFormat === 'landscape' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`} onClick={() => setRenderFormat('landscape')}><span>📺</span> 16:9</button>
           <button className={`px-4 py-2 rounded-md transition-all text-xs font-bold flex gap-2 items-center ${renderFormat === 'portrait' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`} onClick={() => setRenderFormat('portrait')}><span>📱</span> 9:16 <span className="text-[9px] bg-black/30 px-1 rounded text-cyan-300">{t('auto_face')}</span></button>
        </div>
        <div className="flex items-center gap-6">
            <div className="flex items-center gap-3"><span className="text-[10px] font-black uppercase text-slate-400 tracking-tighter">{t('subs')}</span><button onClick={() => setWithSubtitles(!withSubtitles)} className={`w-10 h-5 rounded-full transition-all relative ${withSubtitles ? 'bg-green-500 shadow-lg' : 'bg-slate-700'}`}><div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all shadow-sm ${withSubtitles ? 'left-6' : 'left-1'}`} /></button></div>
            {withSubtitles && (<div className="flex gap-2 bg-slate-900 p-1 rounded-lg border border-slate-700 animate-in fade-in slide-in-from-right-4 duration-300">{[{ id: 'cyan', color: 'bg-cyan-400' }, { id: 'yellow', color: 'bg-yellow-400' }, { id: 'white', color: 'bg-white' }].map((style) => (<button key={style.id} onClick={() => setSubStyle(style.id)} className={`w-6 h-6 rounded-full border-2 transition-all relative group ${style.color} ${subStyle === style.id ? 'border-indigo-500 scale-110 shadow-lg ring-2 ring-indigo-500/30' : 'border-transparent opacity-60 hover:opacity-100 hover:scale-105'}`} />))}</div>)}
        </div>
      </div>
      {(isGenerating || progress > 0) && (<ProgressBar progress={progress} status={wsStatus} />)}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-28">
        {highlights.map((h, idx) => {
          const isSelected = selectedSegments.includes(idx);
          const duration = (h.end - h.start).toFixed(1);
          const paceBadge = duration < 25 ? `⚡ ${t('short')}` : duration > 45 ? `📖 ${t('story')}` : `🎬 ${t('standard')}`;
          const paceColor = duration < 25 ? 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' : duration > 45 ? 'text-blue-400 bg-blue-400/10 border-blue-400/20' : 'text-slate-400 bg-slate-700 border-slate-600';

          return (
            <div key={idx} onClick={() => toggleSegment(idx)} className={`cursor-pointer rounded-xl border transition-all duration-300 group flex flex-col h-full ${isSelected ? 'border-indigo-500 bg-slate-800 shadow-2xl ring-2 ring-indigo-500/50 transform scale-[1.02]' : 'border-slate-700 bg-slate-800/40 hover:border-slate-500'}`}>
              <div className="p-5 relative flex-1 flex flex-col">
                <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-mono bg-black/40 px-2 py-1 rounded text-slate-400 border border-slate-700/50">{Math.floor(h.start)}{t('sec')} → {Math.floor(h.end)}{t('sec')}</span>
                        <span className="text-[10px] font-bold text-indigo-400 bg-indigo-500/10 px-2 py-1 rounded-full border border-indigo-500/20">⏱️ {duration}{t('sec')}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className={`text-[9px] font-black px-2 py-1 rounded shadow-sm border ${paceColor}`}>{paceBadge}</div>
                        <div className={`text-[9px] font-black px-2 py-1 rounded shadow-lg backdrop-blur-sm ${h.importance > 0.8 ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-slate-700 text-slate-400'}`}>{(h.importance * 100).toFixed(0)}% {t('viral')}</div>
                    </div>
                </div>
                <h3 className="font-bold text-base mb-3 text-white leading-tight group-hover:text-indigo-400 transition-colors">{h.title || `${t('clip')} ${idx + 1}`}</h3>
                <p className="text-sm text-slate-300 leading-relaxed border-l-2 border-slate-600 pl-3 italic opacity-80 group-hover:opacity-100 transition-opacity flex-1">"{h.explanation || t('no_analysis')}"</p>
              </div>
              <div className={`p-3 text-center text-[11px] font-black uppercase tracking-widest transition-colors ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-700/30 text-slate-500 group-hover:bg-slate-700/80 group-hover:text-slate-300'}`}>{isSelected ? `✓ ${t('selected')}` : `+ ${t('add')}`}</div>
            </div>
          );
        })}
      </div>
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 w-[95%] max-w-3xl bg-slate-950/90 backdrop-blur-xl border border-slate-700/50 p-4 rounded-2xl shadow-2xl flex items-center justify-between z-50 ring-1 ring-white/10">
        <div className="flex items-center gap-4"><div className="bg-slate-800 w-10 h-10 rounded-full flex items-center justify-center border border-slate-700 text-indigo-400 font-black">{selectedSegments.length}</div><div className="flex flex-col"><span className="text-white font-bold text-sm">{t('clips_ready')}</span><span className="text-[10px] text-slate-500 uppercase tracking-wider">{t('karaoke_pro')} {subStyle}</span></div></div>
        {montageUrl ? (<a href={montageUrl} download className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg active:scale-95 transition-all flex items-center gap-2"><span>💾</span> {t('download')}</a>) : (<button onClick={generateMontage} disabled={isGenerating || selectedSegments.length === 0} className={`px-8 py-3 rounded-xl font-bold text-sm transition-all shadow-xl flex items-center gap-2 ${isGenerating || selectedSegments.length === 0 ? 'bg-slate-800 text-slate-600 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white active:scale-95'}`}>{isGenerating ? <><span className="animate-spin">⚙️</span> {t('rendering')}</> : <><span>✨</span> {t('launch_studio')}</>}</button>)}
      </div>
    </div>
  );
}

const VideoHistory = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('highlights'); 
  const [showUploader, setShowUploader] = useState(false);
  const [credits, setCredits] = useState('...'); 
  const [isPremium, setIsPremium] = useState(false); 
  const [paymentMessage, setPaymentMessage] = useState(null);

  useEffect(() => {
      const query = new URLSearchParams(window.location.search);
      if (query.get("success")) {
          setPaymentMessage({ type: 'success', text: t('payment_success') });
          window.history.replaceState(null, '', window.location.pathname);
          setTimeout(() => {
              fetchCredits();
              setTimeout(() => setPaymentMessage(null), 8000); 
          }, 2000); 
      }
      if (query.get("canceled")) {
          setPaymentMessage({ type: 'error', text: t('payment_canceled') });
          window.history.replaceState(null, '', window.location.pathname);
          setTimeout(() => setPaymentMessage(null), 8000);
      }
  }, [user, t]);

  useEffect(() => { 
      if (user) {
          fetchVideos(); 
          fetchCredits();
      }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCredits = async () => {
      try {
          const res = await fetch(`${API_BASE_URL}/profile/${user.id}`);
          const data = await res.json();
          setCredits(data.credits !== undefined ? data.credits : 0);
          setIsPremium(data.is_premium === true);
      } catch (err) {
          console.error("Erreur crédits:", err);
      }
  };

  const fetchVideos = async () => { 
      try { 
          const response = await fetch(`${API_BASE_URL}/videos?user_id=${user.id}`); 
          if (!response.ok) {
              setVideos([]);
              setLoading(false);
              return;
          }
          const data = await response.json(); 
          setVideos(Array.isArray(data) ? data : []); 
          setLoading(false); 
      } catch (err) { 
          console.error(err); 
          setLoading(false); 
      } 
  };
  
  const fetchVideoDetails = async (videoId, retries = 3) => { 
      try { 
          const response = await fetch(`${API_BASE_URL}/video/${videoId}`); 
          if (!response.ok) {
              if (retries > 0) {
                  setTimeout(() => fetchVideoDetails(videoId, retries - 1), 1000);
                  return;
              }
              throw new Error("Vidéo introuvable");
          }
          const data = await response.json(); 
          setSelectedVideo(data); 
      } catch (err) { console.error("Erreur détails", err); } 
  };

  const deleteVideo = async (e, videoId) => { 
      e.stopPropagation(); 
      if (!window.confirm(t('confirm_delete'))) return; 
      try { 
          setVideos(prev => prev.filter(v => v.id !== videoId)); 
          await fetch(`${API_BASE_URL}/video/${videoId}`, { method: 'DELETE' }); 
          if (selectedVideo?.id === videoId) setSelectedVideo(null); 
      } catch (err) { 
          fetchVideos(); 
      } 
  };

  const handleUploadSuccess = async (newVideoId) => {
    setShowUploader(false);
    await fetchVideos(); 
    fetchVideoDetails(newVideoId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCheckout = async (planType) => {
      try {
          const res = await fetch(`${API_BASE_URL}/create-checkout-session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id: user.id, plan_type: planType })
          });
          const data = await res.json();
          if (data.checkout_url) {
              window.location.href = data.checkout_url;
          } else {
              alert(t('server_error'));
          }
      } catch (err) {
          console.error(err);
          alert(t('network_error'));
      }
  };

  if (!selectedVideo) {
    return (
      <div className="p-6 bg-[#0f172a] min-h-screen animate-in fade-in duration-300">
        <header className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-10 pb-6 border-b border-slate-800">
            <div>
                <h1 className="text-3xl font-black text-white tracking-tight">{t('studio')} <span className="text-indigo-500">Arrow</span></h1>
                <p className="text-slate-400 text-sm mt-1">{t('dashboard')}</p>
            </div>
            
            <div className="flex items-center gap-4 flex-wrap">
                {paymentMessage && (
                    <div className={`px-4 py-1.5 rounded-full text-xs font-bold animate-in slide-in-from-top-4 ${paymentMessage.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                        {paymentMessage.text}
                    </div>
                )}

                {user && (
                    <div className="flex items-center gap-4 mr-2 pr-4 border-r border-slate-700">
                        <div className="bg-slate-950 border border-yellow-500/30 pl-4 pr-1 py-1 rounded-full flex items-center gap-2 shadow-[0_0_15px_rgba(234,179,8,0.15)] cursor-help" title="1 Crédit = 1 Minute de vidéo analysée">
                            <span className="text-yellow-400 text-sm">🟡</span>
                            <span className="text-white font-black text-sm">{credits}</span>
                            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider mr-2">{t('credits')}</span>
                            
                            <button 
                                onClick={() => handleCheckout('pack')} 
                                className="bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-lg transition-colors" 
                                title="Acheter des crédits"
                            >
                                +
                            </button>
                        </div>

                        {isPremium && (
                            <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-black px-2 py-1 rounded uppercase tracking-widest shadow-lg hidden sm:block">
                                {t('pro')}
                            </span>
                        )}

                        <span className="text-xs text-slate-300 font-mono bg-slate-800 px-3 py-1.5 rounded-full border border-slate-700 hidden lg:block">
                            👤 {user.email}
                        </span>
                        <button onClick={onLogout} className="text-xs font-bold text-red-400 hover:text-red-300 transition-colors uppercase tracking-wider">
                            {t('logout')}
                        </button>
                    </div>
                )}

                {!showUploader && (
                    <button 
                        onClick={() => setShowUploader(true)}
                        className="px-5 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 shadow-lg bg-indigo-600 hover:bg-indigo-500 text-white"
                    >
                        🎬 {t('new_project')}
                    </button>
                )}
                {showUploader && (
                    <button 
                        onClick={() => setShowUploader(false)}
                        className="px-5 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 shadow-lg bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700"
                    >
                        ← {t('back')}
                    </button>
                )}

            </div>
        </header>

        {showUploader ? (
            <div className="mt-12">
                <VideoUploader onUploadSuccess={handleUploadSuccess} user={user} onCreditsUpdate={fetchCredits} />
            </div>
        ) : loading ? (
            <div className="text-indigo-400 animate-pulse text-center mt-20 font-bold tracking-widest uppercase">{t('syncing')}</div> 
        ) : videos.length === 0 ? (
            <div className="text-center mt-24 flex flex-col items-center animate-in fade-in zoom-in-95 duration-700">
                <div className="text-7xl mb-6 opacity-30 drop-shadow-[0_0_20px_rgba(255,255,255,0.2)]">📂</div>
                <h3 className="text-3xl text-white font-black mb-3">{t('empty_space')}</h3>
                <p className="text-slate-400 text-base max-w-md mx-auto mb-10">
                    {t('ready_to_transform')} <strong className="text-yellow-400">{credits} {t('credits')}</strong> {t('credits_to_start')}
                </p>
                
                <button 
                    onClick={() => setShowUploader(true)}
                    className="group relative bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-black text-lg py-5 px-10 rounded-2xl shadow-[0_0_40px_rgba(99,102,241,0.3)] hover:shadow-[0_0_60px_rgba(99,102,241,0.5)] hover:scale-105 active:scale-95 transition-all duration-300 flex items-center gap-4 overflow-hidden"
                >
                    <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:animate-[shimmer_1.5s_infinite]"></div>
                    <span className="text-3xl group-hover:rotate-12 transition-transform duration-300">🎬</span>
                    <span>{t('create_first')}</span>
                </button>
            </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map(vid => (
              <div key={vid.id} onClick={() => fetchVideoDetails(vid.id)} className="group bg-slate-900 border border-slate-800 p-4 rounded-2xl cursor-pointer hover:border-indigo-500/50 hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-300 relative overflow-hidden flex flex-col h-full">
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60 pointer-events-none" />
                <div className="bg-slate-950 rounded-xl mb-4 flex items-center justify-center border border-slate-800 group-hover:border-slate-700 transition-colors relative min-h-[9rem]"><span className="text-5xl opacity-20 group-hover:opacity-40 transition-opacity grayscale group-hover:grayscale-0">🎬</span><div className="absolute top-2 right-2 bg-slate-900/80 backdrop-blur px-2 py-1 rounded text-[10px] font-mono text-slate-400 border border-slate-800">{Math.round(vid.audio_duration / 60)} {t('min')}</div></div>
                <h3 className="text-slate-100 font-bold mb-1 pr-8 truncate">{vid.filename.replace(/^\d+_/, '')}</h3>
                <p className="text-xs text-slate-500 mb-6 font-mono">{new Date(vid.created_at).toLocaleDateString()}</p>
                <div className="mt-auto flex gap-2"><button onClick={(e) => deleteVideo(e, vid.id)} className="flex-1 py-2 bg-slate-800 hover:bg-red-500/20 text-slate-500 hover:text-red-400 rounded-lg text-[10px] font-bold transition-all border border-slate-700 hover:border-red-500/30">{t('delete')}</button><button className="flex-1 py-2 bg-indigo-600/10 hover:bg-indigo-600 text-indigo-400 hover:text-white rounded-lg text-[10px] font-bold transition-all border border-indigo-500/20 hover:border-indigo-500">{t('open')}</button></div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a] animate-in fade-in duration-500">
      <div className="bg-slate-900/50 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
           <div className="flex items-center gap-4"><button onClick={() => setSelectedVideo(null)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded-lg text-sm transition-colors border border-slate-700">← {t('back')}</button><div><h1 className="text-lg font-bold text-white truncate max-w-[200px] md:max-w-md">{selectedVideo.filename.replace(/^\d+_/, '')}</h1><div className="flex gap-2 text-[10px] text-slate-500 font-mono"><span>ID: {selectedVideo.id}</span><span>•</span><span>DURATION: {selectedVideo.audio_duration}{t('sec')}</span></div></div></div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
            <div className="bg-black rounded-2xl overflow-hidden shadow-2xl border border-slate-800 relative group"><video controls src={selectedVideo.video_url} className="w-full h-auto max-h-[500px] object-contain mx-auto" /></div>
            <div className="flex gap-8 border-b border-slate-800 px-2">
                <button className={`pb-4 text-sm font-bold transition-all relative ${activeTab === 'highlights' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`} onClick={() => setActiveTab('highlights')}>✂️ {t('ai_clips')}{activeTab === 'highlights' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-500 rounded-t-full" />}</button>
                <button className={`pb-4 text-sm font-bold transition-all relative ${activeTab === 'summary' ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`} onClick={() => setActiveTab('summary')}>💡 {t('summary')}{activeTab === 'summary' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-500 rounded-t-full" />}</button>
            </div>
            {activeTab === 'summary' && (<div className="bg-slate-900 p-8 rounded-2xl border border-slate-800 text-slate-200 shadow-xl"><h2 className="text-lg font-bold mb-4 text-white flex items-center gap-2 text-indigo-400">📌 {t('exec_summary')}</h2><p className="mb-8 leading-relaxed text-slate-300 text-base border-l-2 border-indigo-500/30 pl-4">{selectedVideo.summary?.general_summary || t('no_analysis')}</p><h2 className="text-lg font-bold mb-4 text-white flex items-center gap-2 text-indigo-400">🔑 {t('key_points')}</h2><ul className="space-y-3">{selectedVideo.summary?.key_points?.map((k, i) => (<li key={i} className="flex gap-3 text-slate-300 text-sm leading-snug bg-slate-800/50 p-3 rounded-lg border border-slate-800"><span className="text-indigo-500 font-bold mt-0.5">•</span> {k}</li>))}</ul></div>)}
            {activeTab === 'highlights' && (<MontageGenerator videoId={selectedVideo.id} initialHighlights={selectedVideo.highlights} />)}
        </div>
        <div className="lg:col-span-4 space-y-6">
            <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 shadow-xl sticky top-24">
                <h3 className="text-white font-black mb-6 text-xs uppercase tracking-widest flex items-center gap-2"><span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>{t('prod_assistant')}</h3>
                <div className="space-y-5 text-xs font-mono">
                    <div className="flex justify-between items-center border-b border-slate-800 pb-3"><span className="text-slate-500">{t('cut_mode')}</span><span className="text-indigo-400 font-bold flex items-center gap-1">{t('smart_timeline')}</span></div>
                    <div className="flex justify-between items-center border-b border-slate-800 pb-3"><span className="text-slate-500">{t('ui_mode')}</span><span className="text-white font-bold flex items-center gap-1">{t('dynamic_pace')}</span></div>
                    <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                        <span className="text-slate-500">{t('plan')}</span>
                        {isPremium ? (
                            <span className="text-amber-500 font-bold flex items-center gap-1">{t('pro')}</span>
                        ) : (
                            <span className="text-slate-400 font-bold flex items-center gap-1">{t('free')}</span>
                        )}
                    </div>
                </div>
                <div className="mt-6 space-y-3">
                    {!isPremium && (
                        <button onClick={() => handleCheckout('pro')} className="w-full bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 text-white font-black py-3 px-4 rounded-xl shadow-lg active:scale-95 transition-all text-sm flex items-center justify-center gap-2">
                            <span>✨</span> {t('upgrade_pro')}
                        </button>
                    )}
                    <button onClick={() => handleCheckout('pack')} className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg active:scale-95 transition-all text-sm border border-slate-700 flex items-center justify-center gap-2">
                        <span>🟡</span> {t('buy_credits')}
                    </button>
                </div>
                <div className="mt-8 p-4 bg-indigo-900/20 rounded-xl border border-indigo-500/20"><p className="text-[11px] text-indigo-300 leading-relaxed italic">"{t('eco_mode')}"</p></div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default VideoHistory;